import styled from '@emotion/styled';
import { touch } from '../../style-utils/media-queries';
import { css } from '@emotion/react';
import {
  gradient,
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
  layout,
  duration,
  easing,
} from '../../style-utils/theme';
import {
  auroraShift,
  skeletonBackground,
  shimmerLoop,
  LOOP,
} from '../../style-utils/motion';
import { SCALE } from '../../motion/tokens';

// Shared styled layer for Lobbero IQ. gradient.aurora / auroraSoft are reserved
// EXCLUSIVELY for these surfaces (design_system §gradient). Never a raw hex.

export const IqPage = styled.div`
  /* vh first as the fallback, dvh second to override it — design_system.md
     section 8 rule 7. NO BACKTICKS IN THIS COMMENT: it sits inside a styled
     template literal and one would silently terminate the string. */
  min-height: 100vh;
  min-height: 100dvh;
  background: ${palette.porcelain};
  padding: ${spacing.md} ${spacing.sm} ${spacing.xxl};
`;

export const IqInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const IqHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

export const IqTitle = styled.h1`
  font-family: ${typography.display};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  color: ${palette.ink};
  letter-spacing: ${typography.tracking.snug};
  margin: 0;
`;

export const IqSub = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  margin: ${spacing.half} 0 0;
`;

export const IqCard = styled.section<{ $flush?: boolean }>`
  background: ${gradient.auroraSoft}, ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.md};
  overflow: hidden;

  /* ONE FRAME PER SURFACE. Hosted inside a card that already draws a border —
     the clock-in gate's ShiftCard, the handover modal — this card's own border
     and shadow doubled the frame: two hairlines a few pixels apart around the
     same content. The aurora tint and the radius are identity enough there;
     the HOST owns the frame. */
  ${p => (p.$flush ? 'border: none; box-shadow: none;' : '')}
`;

export const AuroraHeader = styled.div<{ $running?: boolean }>`
  background: ${gradient.aurora};
  background-size: 200% 200%;
  ${p =>
    p.$running
      ? css`
          animation: ${auroraShift} ${LOOP.aurora} ${easing.standard} infinite;
        `
      : ''}
  color: ${palette.white};
  padding: ${spacing.sm} ${spacing.md};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base}px;
`;

export const Spark = styled.span`
  color: ${palette.irisGlow};
  font-size: ${typography.size.md}px;
  line-height: 1;
`;

export const CardBody = styled.div`
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const Headline = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  line-height: ${typography.leading.snug};
  margin: 0;
`;

export const SectionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.sm} 0;
  border-top: 1px solid ${palette.line};
`;

export const RowHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  flex-wrap: wrap;
`;

export const RowText = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.inkSoft};
  line-height: ${typography.leading.normal};
  margin: 0;
`;

export const SourceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
`;

export const SourceChip = styled.button`
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid ${palette.line};
  background: ${status.neutral.bg};
  color: ${status.neutral.fg};
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  cursor: pointer;
  &:hover {
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const Caption = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  margin: 0;
`;

export const CountsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
  padding-top: ${spacing.xs};
`;

export const Band = styled.div<{ $tone: keyof typeof status }>`
  background: ${p => status[p.$tone].bg};
  color: ${p => status[p.$tone].fg};
  border-radius: ${radius.sm};
  padding: ${spacing.xs} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
`;

export const AuroraButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  border: none;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.md};
  border-radius: ${radius.pill};
  background: ${gradient.aurora};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  box-shadow: ${shadow.brand};
  cursor: pointer;
  transition: box-shadow ${duration.base} ${easing.standard},
    transform ${duration.base} ${easing.spring};
  &:hover:not(:disabled) {
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
    transition-duration: ${duration.instant};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
    box-shadow: none;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const GhostButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.compactControlHeight};
  /* Coarse pointer: keep the compact look, meet the 44px floor. A desk tablet
     is this product's primary device, so a 32px control here is the common
     case and not the edge one. NO BACKTICKS IN THIS COMMENT - it lives inside a
     styled template literal. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${palette.lineStrong};
    color: ${palette.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const SkeletonRow = styled.div`
  height: 16px;
  border-radius: ${radius.xs};
  ${skeletonBackground}
  ${shimmerLoop}
`;
